import {
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogRoot,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "reka-ui";
import { defineComponent } from "vue";
import { forwardEmit } from "@/utils";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

const Root = AlertDialogRoot;

const Trigger = defineComponent({
  name: "AlertDialogTrigger",

  props: {
    asChild: p.boolean(),
    as: p.string<keyof HTMLElementTagNameMap>("button"),
  },
  setup(props, { slots }) {
    return () => <AlertDialogTrigger {...props}>{slots.default?.()}</AlertDialogTrigger>;
  },
});

const Content = defineComponent({
  name: "AlertDialogContent",
  inheritAttrs: false,
  emits: {
    closeAutoFocus: (_e: Event) => true,
    dismiss: () => true,
    escapeKeyDown: (_e: Event) => true,
    focusOutside: (_e: Event) => true,
    interactOutside: (_e: Event) => true,
    openAutoFocus: (_e: Event) => true,
    pointerDownOutside: (_e: Event) => true,
  },
  props: {
    asChild: p.boolean(),
    as: p.string<keyof HTMLElementTagNameMap>(),
    disableOutsidePointerEvents: p.boolean(false),
    forceMount: p.boolean(false),
    trapFocus: p.boolean(true),
  },
  setup(props, { slots, emit, attrs }) {
    const emits = forwardEmit(emit);

    return () => (
      <AlertDialogPortal>
        <AlertDialogOverlay class="dark:bg-gray-950/90 fixed inset-0 z-80 bg-background/80 text-muted-foreground backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
          <AlertDialogContent
            {...props}
            {...emits}
            class={cn(
              "fixed left-[50%] top-[50%] z-80 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-card p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg md:w-full",
              attrs.class ?? ""
            )}
          >
            {slots.default?.()}
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialogPortal>
    );
  },
});

const Header = defineComponent({
  name: "AlertDialogHeader",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <div class={cn("flex flex-col space-y-2 text-center sm:text-left", props.class)}>
        {slots.default?.()}
      </div>
    );
  },
});

const Title = defineComponent({
  name: "AlertDialogTitle",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>("h2"),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => (
      <AlertDialogTitle
        asChild={props.asChild}
        as={props.as}
        class={cn("text-lg font-semibold text-foreground", props.class)}
      >
        {slots.default?.()}
      </AlertDialogTitle>
    );
  },
});

const Description = defineComponent({
  name: "AlertDialogDescription",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>("p"),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => (
      <AlertDialogDescription {...props} class={cn("text-foreground-muted text-sm", props.class)}>
        {slots.default?.()}
      </AlertDialogDescription>
    );
  },
});

const Footer = defineComponent({
  name: "AlertDialogFooter",
  props: {
    class: p.string(),
  },
  setup(props, { slots }) {
    return () => (
      <div
        class={cn(
          "mt-3.5 flex flex-col space-y-2 sm:flex-row sm:justify-end sm:space-x-2 sm:space-y-0",
          props.class
        )}
      >
        {slots.default?.()}
      </div>
    );
  },
});

const Action = defineComponent({
  name: "AlertDialogAction",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>("button"),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => <AlertDialogAction asChild={props.asChild}>{slots.default?.()}</AlertDialogAction>;
  },
});

const Cancel = defineComponent({
  name: "AlertDialogCancel",
  props: {
    class: p.string(),
    as: p.string<keyof HTMLElementTagNameMap>("button"),
    asChild: p.boolean(),
  },
  setup(props, { slots }) {
    return () => <AlertDialogCancel asChild={props.asChild}>{slots.default?.()}</AlertDialogCancel>;
  },
});

export default {
  Root,
  Trigger,
  Content,
  Header,
  Title,
  Description,
  Footer,
  Action,
  Cancel,
};
